import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { BuildingWithConvertedAddresses } from '../utils/types';
import './BuildingLocationMap.scss';

interface BuildingLocationMapProps {
  selectedBuilding: BuildingWithConvertedAddresses;
}

const BuildingLocationMap: React.FC<BuildingLocationMapProps> = ({ selectedBuilding }) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current || !selectedBuilding.centroid) return;

    // Initialize map
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN!;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`,
      center: [selectedBuilding.centroid.lng, selectedBuilding.centroid.lat],
      zoom: 16,
    });

    // Add navigation controls
    mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Create a custom marker element
    const markerElement = document.createElement('div');
    markerElement.className = 'building-location-marker';
    markerElement.innerHTML = `
      <svg fill="none" height="40px" viewBox="0 0 24 24" width="40px" xmlns="http://www.w3.org/2000/svg" id="fi_11515653"><circle cx="12" cy="12" r="8" fill="white"/><path d="m12 0c-4.687 0-8.5 3.813-8.5 8.5 0 8.218 7.839 15.09 8.173 15.378.094.081.21.122.327.122s.233-.041.327-.122c.333-.289 8.173-7.161 8.173-15.378 0-4.687-3.813-8.5-8.5-8.5zm4.462 8.691c-.077.187-.26.309-.462.309h-.5v3.5c0 .276-.224.5-.5.5h-2c-.276 0-.5-.224-.5-.5v-2.5h-1v2.5c0 .276-.224.5-.5.5h-2c-.276 0-.5-.224-.5-.5v-3.5h-.5c-.202 0-.385-.122-.462-.309-.078-.187-.035-.402.108-.545l4-4c.195-.195.512-.195.707 0l4 4c.144.144.187.358.109.545z" fill="rgb(0,0,0)"></path></svg>
    `;

    // Add marker to map
    markerRef.current = new mapboxgl.Marker(markerElement, {
      offset: [0, -12] // [x, y] offset in pixels - negative y moves marker up
    })
      .setLngLat([selectedBuilding.centroid.lng, selectedBuilding.centroid.lat])
      .addTo(mapRef.current);

    // Add click handler to recenter map on marker
    markerElement.addEventListener('click', () => {
      mapRef.current?.flyTo({
        center: [selectedBuilding.centroid.lng, selectedBuilding.centroid.lat],
        zoom: 16,
      });
    });

    // Cleanup
    return () => {
      markerRef.current?.remove();
      mapRef.current?.remove();
    };
  }, [selectedBuilding]);

  return (
    <div className="building-location-map-container">
      <div ref={mapContainerRef} className="building-location-map" />
    </div>
  );
};

export default BuildingLocationMap; 